import { useRef } from 'react';
import { ERAS, MISSION_OBJECTIVES, STORY_CHAPTERS, STORY_MODE_CHAPTERS } from '../game/story.js';
import { ROUTE, NEXT_CHAPTER } from '../game/storyAdapter.js';
import { SENSITIVITY, SMOOTHING } from '../game/settings.js';

function BrandMark() {
  return (
    <div className="brand-mark" aria-label="The Time Gauntlet">
      <span className="brand-mark__sigil" aria-hidden="true">
        ⌛
      </span>
      <span>
        <strong>The Time Gauntlet</strong>
        <small>A Kronopolis story</small>
      </span>
    </div>
  );
}

function Slider({ label, hint, value, range, format, onChange }) {
  return (
    <label className="tuning__row">
      <span className="tuning__label">
        {label}
        <b>{format(value)}</b>
      </span>
      <input
        type="range"
        min={range.min}
        max={range.max}
        step={range.step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <small>{hint}</small>
    </label>
  );
}

/** Look tuning. Shown in the briefing and again on the pause panel. */
function LookTuning({ settings, onChange }) {
  return (
    <fieldset className="tuning">
      <legend>Look controls</legend>

      <Slider
        label="Sensitivity"
        hint="How far the view turns when you drag or move the mouse."
        value={settings.sensitivity}
        range={SENSITIVITY}
        format={(value) => `${value.toFixed(2)}×`}
        onChange={(sensitivity) => onChange({ sensitivity })}
      />

      <Slider
        label="Smoothing"
        hint="Higher values ease the camera into each turn. Zero is raw and immediate."
        value={settings.smoothing}
        range={SMOOTHING}
        format={(value) => `${Math.round(value * 100)}%`}
        onChange={(smoothing) => onChange({ smoothing })}
      />

      <label className="tuning__toggle">
        <input
          type="checkbox"
          checked={settings.invertY}
          onChange={(event) => onChange({ invertY: event.target.checked })}
        />
        <span>Invert vertical look</span>
      </label>
    </fieldset>
  );
}

function Menu({ onBeginStory, saved, onContinue }) {
  return (
    <section className="menu-screen screen-layer">
      <div className="menu-screen__copy">
        <p className="eyebrow">Playable route · {ROUTE.title}</p>
        <h1>
          Steal back
          <br />
          <em>time itself.</em>
        </h1>
        <p className="lede">
          Kronos owns every clock in the city. Sixteen-year-old thief Mikey Zillman knows the one
          route into his fortress—and tonight, his crew is depending on him.
        </p>
        <div className="menu-screen__actions">
          <button className="button button--primary" type="button" onClick={onBeginStory}>
            Begin the story <span aria-hidden="true">→</span>
          </button>
          {saved && (
            <button className="button button--ghost" onClick={onContinue}>
              Continue checkpoint
            </button>
          )}
          <span className="runtime-note">Interactive 3D · Best with headphones</span>
        </div>
      </div>

      <aside className="mission-teaser" aria-label="Tonight's mission">
        <span className="mission-teaser__number">{String(STORY_MODE_CHAPTERS.indexOf(ROUTE) + 1).padStart(2, '0')}</span>
        <div>
          <small>Tonight’s mission</small>
          <strong>Reach the fortress approach</strong>
          <p>Cross the roofs. Avoid the lights. Reach the fortress approach.</p>
        </div>
      </aside>
    </section>
  );
}

function Story({ chapter, chapterIndex, onNext, onPrevious, onSkip }) {
  const finalChapter = chapterIndex === STORY_CHAPTERS.length - 1;
  const swipe = useRef(null);

  return (
    <section className="story-screen screen-layer" aria-live="polite">
      <article
        className="story-card"
        onPointerDown={(event) => {
          if (event.target.closest('button')) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          swipe.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => {
          if (!swipe.current) return;
          const dx = event.clientX - swipe.current.x,
            dy = event.clientY - swipe.current.y;
          swipe.current = null;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
            if (dx < 0) onNext();
            else onPrevious();
          }
        }}
        onPointerCancel={() => {
          swipe.current = null;
        }}
      >
        <div
          className="story-card__image"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(3, 6, 10, .1), rgba(3, 6, 10, .92)), url(${chapter.image})`,
          }}
          role="img"
          aria-label={`${chapter.title} concept art`}
        />
        <div className="story-card__content">
          <div className="story-card__count">
            <span>{String(chapterIndex + 1).padStart(2, '0')}</span>
            <span>/</span>
            <span>{String(STORY_CHAPTERS.length).padStart(2, '0')}</span>
          </div>
          <p className="eyebrow">{chapter.eyebrow}</p>
          <h2>{chapter.title}</h2>
          <p>{chapter.summary}</p>
          <div className="story-card__actions">
            <button className="button button--ghost" type="button" onClick={onPrevious}>
              Back
            </button>
            <button className="button button--primary" type="button" onClick={onNext}>
              {finalChapter ? 'See the plan' : 'Continue'} <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </article>

      <nav className="chapter-rail" aria-label="Story chapters">
        {STORY_CHAPTERS.map((item, index) => (
          <span
            className={
              index <= chapterIndex ? 'chapter-rail__item is-active' : 'chapter-rail__item'
            }
            key={item.id}
            aria-label={`${index < chapterIndex ? 'Completed' : index === chapterIndex ? 'Current' : 'Upcoming'}: ${item.title}`}
          >
            <i />
            <b>{item.eyebrow}</b>
          </span>
        ))}
      </nav>

      <button className="text-button story-skip" type="button" onClick={onSkip}>
        Skip to mission briefing
      </button>
    </section>
  );
}

function Briefing({ lookSettings, onLookSettingsChange, onBeginMission, touch }) {
  return (
    <section className="briefing-screen screen-layer">
      <div className="briefing-panel">
        <p className="eyebrow">Mission briefing · 23:47</p>
        <h2>The fortress route</h2>
        <p className="briefing-panel__intro">
          Reach the fortress approach with your crew. Lift the practice wrench from the training
          dummy, open the service grate, and lower the ladder. The heist comes next.
        </p>

        <ol className="objective-list">
          {MISSION_OBJECTIVES.map((objective, index) => (
            <li key={objective.id}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <strong>{objective.label}</strong>
                <p>{objective.detail}</p>
              </div>
            </li>
          ))}
        </ol>

        {touch && (
          <p className="touch-legend">
            Left stick: move. Drag the world: look. Tap Sprint to toggle, Jump to hop, and Interact
            near a gold marker. Tap Pause to stop. Lift the practice wrench, open the service grate,
            then lower the ladder.
          </p>
        )}
        <div className="control-strip" aria-label="Controls">
          <span>
            <kbd>WASD</kbd> Move
          </span>
          <span>
            <kbd>Mouse</kbd> Look
          </span>
          <span>
            <kbd>Shift</kbd> Sprint
          </span>
          <span>
            <kbd>Space</kbd> Jump
          </span>
          <span>
            <kbd>E</kbd> Interact
          </span>
          <span>
            <kbd>Q / R</kbd> Freeze / rewind once acquired
          </span>
          <span>
            <kbd>Esc</kbd> Pause
          </span>
        </div>

        <details className="look-settings">
          <summary>Adjust look controls</summary>
          <LookTuning settings={lookSettings} onChange={onLookSettingsChange} />
        </details>

        <button
          id="enter-world-button"
          className="button button--primary button--wide"
          type="button"
          onClick={onBeginMission}
        >
          Enter Kronopolis <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
}

/** Shown whenever the pointer lock is released mid-mission. */
function Paused({ lookSettings, onLookSettingsChange, onResume, contextLost }) {
  return (
    <section className="pause-screen screen-layer">
      <div className="pause-panel">
        <p className="eyebrow">Paused</p>
        <h2>{contextLost ? 'Restoring the view' : 'Take a breath.'}</h2>
        <p>
          {contextLost
            ? 'The graphics context was lost. Wait for recovery, then resume. If it does not return, reload and continue your checkpoint.'
            : 'Adjust the feel, then continue from where you paused.'}
        </p>

        <LookTuning settings={lookSettings} onChange={onLookSettingsChange} />

        <button
          className="button button--primary button--wide"
          type="button"
          onClick={onResume}
          disabled={contextLost}
        >
          Resume
        </button>
      </div>
    </section>
  );
}

function GameHud({ era, objective, objectiveIndex, gauntlet }) {
  return (
    <section className="game-hud screen-layer" aria-live="polite">
      <div className="objective-card">
        <p className="eyebrow">Current objective</p>
        <strong>{objective?.label ?? 'Reach the fortress'}</strong>
        <p>{objective?.detail}</p>
        <div
          className="objective-progress"
          aria-label={`Objective ${objectiveIndex + 1} of ${MISSION_OBJECTIVES.length}`}
        >
          {MISSION_OBJECTIVES.map((item, index) => (
            <i key={item.id} className={index <= objectiveIndex ? 'is-active' : ''} />
          ))}
        </div>
      </div>

      {gauntlet && (
        <div className="time-status">
          <div className="time-status__ring" aria-hidden="true">
            <span />
          </div>
          <div>
            <small>Temporal state</small>
            <strong>{era.label}</strong>
            <span>Q freeze · R rewind</span>
          </div>
        </div>
      )}
      <div className="crosshair" aria-hidden="true">
        <i />
      </div>
    </section>
  );
}

function Complete({ onReplay, onContinue }) {
  return (
    <section className="complete-screen screen-layer">
      <div className="complete-panel">
        <span className="complete-panel__icon" aria-hidden="true">
          ✓
        </span>
        <p className="eyebrow">{ROUTE.title} complete</p>
        <h2>The approach is clear.</h2>
        <p>
          Mikey reaches the fortress approach. His crew takes the front entrance; he will find the
          back vents.
        </p>
        <div className="complete-panel__next">
          <small>Next chapter</small>
          <strong>{NEXT_CHAPTER.title}</strong>
          <span>{NEXT_CHAPTER.availability === 'planned' ? 'Coming soon' : 'Available'}</span>
        </div>
        <button className="button button--primary" type="button" onClick={onContinue}>
          Continue from checkpoint
        </button>
        <button className="button button--ghost" type="button" onClick={onReplay}>
          Replay the opening
        </button>
      </div>
    </section>
  );
}

export function Interface({
  phase,
  chapter,
  chapterIndex,
  objective,
  objectiveIndex,
  era,
  lookSettings,
  paused,
  touch,
  gauntlet,
  saved,
  contextLost,
  onResume,
  onContinue,
  onLookSettingsChange,
  onBeginStory,
  onNextChapter,
  onPreviousChapter,
  onSkipStory,
  onBeginMission,
  onReplay,
}) {
  const playing = phase === 'playing';

  return (
    <div className="interface">
      <BrandMark />
      <div className="chapter-label">Kronopolis</div>
      {phase === 'menu' && (
        <Menu onBeginStory={onBeginStory} saved={saved} onContinue={onContinue} />
      )}
      {phase === 'story' && (
        <Story
          chapter={chapter}
          chapterIndex={chapterIndex}
          onNext={onNextChapter}
          onPrevious={onPreviousChapter}
          onSkip={onSkipStory}
        />
      )}
      {phase === 'briefing' && (
        <Briefing
          touch={touch}
          lookSettings={lookSettings}
          onLookSettingsChange={onLookSettingsChange}
          onBeginMission={onBeginMission}
        />
      )}
      {playing && (
        <GameHud
          gauntlet={gauntlet}
          era={era ?? ERAS[0]}
          objective={objective}
          objectiveIndex={objectiveIndex}
        />
      )}
      {playing && paused && (
        <Paused
          onResume={onResume}
          contextLost={contextLost}
          lookSettings={lookSettings}
          onLookSettingsChange={onLookSettingsChange}
        />
      )}
      {phase === 'complete' && <Complete onReplay={onReplay} onContinue={onContinue} />}
      <div className="grain" aria-hidden="true" />
    </div>
  );
}
